'use client'

import { useTranslations } from 'next-intl'
import type { ReactNode, RefObject } from 'react'
import { use } from 'react'

import SeeAllLink from '@/app/[locale]/(worlds)/components/SeeAllLink'
import Swimlane, { SwimlaneSkeleton } from '@/app/[locale]/(worlds)/components/Swimlane'
import ViverseWorldCard from '@/components/ViverseWorldCard'
import { ViverseWorldCardSkeleton } from '@/components/ViverseWorldCardSkeleton'
import IconExperimence from '@/components/assets/icon_experimence.svg'
import IconGamepad from '@/components/assets/icon_gamepad.svg'
import IconVideo from '@/components/assets/icon_video.svg'
import { CARD_CLASS_SWIMLANE } from '@/constants/card'
import { SWIMLANE_DEFAULT_CONTENT_INSET_CLASS } from '@/constants/swimlane'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useDraggingScrollMotion } from '@/hooks/useDraggingScrollMotion'
import { extractRoomIdFromUrl } from '@/lib/cms'
import { cn } from '@/lib/utils'
import type { ViverseWorldCardType, WorldCardInfo } from '@/types/viverse-world-card'

const LANDSCAPE_CARD_LIMIT = 8

type HighlightVariant = 'games' | 'videos' | 'experiences'

type HighlightConfig = {
  dataSection: string
  titleKey: 'top-onboarding.games' | 'top-onboarding.videos' | 'top-onboarding.experiences'
  href?: string
  icon: ReactNode
  cardType: ViverseWorldCardType
  cardClassName?: string
}

const HIGHLIGHT_CONFIG: Record<HighlightVariant, HighlightConfig> = {
  games: {
    dataSection: 'top-onboarding-worlds',
    titleKey: 'top-onboarding.games',
    href: '/games',
    icon: <IconGamepad className="size-6 shrink-0 fill-white" />,
    cardType: 'games',
  },
  videos: {
    dataSection: 'popular-videos',
    titleKey: 'top-onboarding.videos',
    href: '/videos',
    icon: <IconVideo className="size-6 shrink-0 fill-white" />,
    cardType: 'videos',
  },
  experiences: {
    dataSection: 'featured-experiences',
    titleKey: 'top-onboarding.experiences',
    href: '?type=experience#explore-viverse',
    icon: <IconExperimence className="size-6 shrink-0 fill-white" />,
    cardType: 'experiences',
    cardClassName: 'h-[169px] min-w-[200px] max-w-[200px] flex-none',
  },
}

type HighlightSectionProps = {
  roomsPromise: Promise<WorldCardInfo[]>
  variant: HighlightVariant
  className?: string
  scrollerClassName?: string
  contentInsetClassName?: string
  priorityFirstCard?: boolean
}

const getRoomHref = (room: WorldCardInfo) =>
  extractRoomIdFromUrl(room.external_link || room.hubs_url || '/')

const getRoomsWindow = (rooms: WorldCardInfo[], start: number, limit: number) => {
  const selectedRooms = rooms.slice(start, start + limit)

  if (selectedRooms.length > 0) {
    return selectedRooms
  }

  return rooms.slice(0, limit)
}

function HighlightCardsScroller({
  scrollRef,
  rooms,
  cardType,
  cardClassName = CARD_CLASS_SWIMLANE,
  className,
  contentInsetClassName,
  priorityFirstCard,
  enableDraggingScroll,
}: {
  scrollRef: RefObject<HTMLDivElement | null>
  rooms: WorldCardInfo[]
  cardType: ViverseWorldCardType
  cardClassName?: string
  className?: string
  contentInsetClassName?: string
  priorityFirstCard?: boolean
  enableDraggingScroll?: boolean
}) {
  const { store } = useConfigsContext()
  const htcToken = store?.credentials?.htcToken

  useDraggingScrollMotion(scrollRef)

  return (
    <div
      ref={scrollRef}
      className={cn(
        'no-scrollbar flex w-full flex-1 gap-2 overflow-x-auto select-none md:gap-4',
        enableDraggingScroll && 'cursor-grab',
        cardType === 'experiences' && '-mb-8 pt-2 pb-10',
        contentInsetClassName,
        className,
      )}
    >
      {rooms.map((room, index) => (
        <ViverseWorldCard
          key={`highlight-${cardType}-${room.id}-${index}`}
          info={room}
          data-room-id={room.id ? `hl-${cardType}:${room.id}` : undefined}
          href={getRoomHref(room)}
          className={cn(cardClassName, index === rooms.length - 1 && 'mr-4 lg:mr-6')}
          cardType={cardType}
          priority={priorityFirstCard && index === 0}
          imageSizes={priorityFirstCard && index === 0 ? '320px' : undefined}
          hideLiked={!htcToken}
          hidePremiumBadge={false}
        />
      ))}
    </div>
  )
}

export default function HighlightSection({
  roomsPromise,
  variant,
  className,
  scrollerClassName,
  contentInsetClassName,
  priorityFirstCard,
}: HighlightSectionProps) {
  const t = useTranslations('worlds-landing')
  const rooms = use(roomsPromise)

  const { dataSection, titleKey, href, icon, cardType, cardClassName } = HIGHLIGHT_CONFIG[variant]

  if (rooms.length === 0) {
    return null
  }

  const visibleRooms = getRoomsWindow(rooms, 0, LANDSCAPE_CARD_LIMIT)

  return (
    <Swimlane
      data-section={dataSection}
      className={cn('gap-3', className)}
      contentInsetClassName={contentInsetClassName}
      title={
        <div className="md:subheader-2 flex items-center gap-1.5 font-semibold md:font-semibold">
          {icon}
          <span>{t(titleKey)}</span>
        </div>
      }
      titleAction={href ? <SeeAllLink href={href} label={t('common.see-all')} /> : undefined}
      renderContent={(scrollRef, enableDraggingScroll, contentInsetClassName) => (
        <HighlightCardsScroller
          scrollRef={scrollRef}
          rooms={visibleRooms}
          cardType={cardType}
          cardClassName={cardClassName}
          contentInsetClassName={contentInsetClassName}
          className={scrollerClassName}
          priorityFirstCard={priorityFirstCard}
          enableDraggingScroll={enableDraggingScroll}
        />
      )}
    />
  )
}

export function HighlightSectionSkeleton({
  cardClassName = CARD_CLASS_SWIMLANE,
  className,
  withPyOffset = false,
}: {
  cardClassName?: string
  className?: string
  withPyOffset?: boolean
}) {
  return (
    <SwimlaneSkeleton showTitleAction className={cn('gap-3', className)}>
      <div
        className={cn(
          'no-scrollbar flex w-full flex-1 gap-2 overflow-x-auto md:gap-4',
          SWIMLANE_DEFAULT_CONTENT_INSET_CLASS,
          withPyOffset && 'py-2',
        )}
      >
        {Array.from({ length: 5 }).map((_, index) => (
          <ViverseWorldCardSkeleton key={`skeleton-${index}`} className={cardClassName} />
        ))}
      </div>
    </SwimlaneSkeleton>
  )
}
